/**
 * Custom hook for buyer form management using React Hook Form
 * Unified hook for both create and edit operations
 */

import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import type { BuyerFormData, BuyerFormMode } from '../types';
import { buyerFormResolver } from '../validation/buyerFormValidation';

interface UseBuyerFormProps {
  /** Initial form data for edit mode */
  initialData?: Partial<BuyerFormData>;
  /** Form mode (create or edit) */
  mode: BuyerFormMode;
  /**
   * Form submission handler. Returns the persisted values on success, nothing
   * on failure.
   */
  onSubmit: (data: BuyerFormData) => Promise<BuyerFormData | undefined>;
}

export const useBuyerForm = ({
  initialData,
  mode,
  onSubmit,
}: UseBuyerFormProps) => {
  const form = useForm<BuyerFormData>({
    mode: 'all',
    defaultValues: {
      name: '',
      displayName: '',
      fullAddress: '',
      deliveryComment: '',
      addressCity: '',
      addressCountry: '',
      isTest: false,
      addressLatitude: undefined,
      addressLongitude: undefined,
      addressUTCOffset: undefined,
      addressSource: 'custom',
      addressGooglePlaceId: undefined,
      addressGoogleAddressId: undefined,
      ...initialData,
    },
    resolver: buyerFormResolver,
    // Read by the schema as `$mode`: the address-coordinate requirement applies
    // to creation only (see buyerFormValidation).
    context: { mode },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
    reset,
    setValue,
    watch,
    getValues,
  } = form;

  const handleFormSubmit = useCallback(
    async (data: BuyerFormData) => {
      // Copied before awaiting. react-hook-form hands the same object to the
      // handler that `setValue` writes into, so a Places pick made while the
      // save is in flight mutates `data` too. Comparing against it then finds no
      // difference and the reset throws the new address away.
      const submitted = { ...data };

      let saved: BuyerFormData | undefined;
      try {
        saved = await onSubmit(submitted);
      } catch {
        // Stop here on failure, for two reasons (CRM-376):
        // 1. react-hook-form 7.48.2 has no try/catch around this handler, so a
        //    rejection skips its `isSubmitting: false` update and the submit
        //    button spins for ever.
        // 2. The values the user typed are the only copy of them — clearing the
        //    form would delete the thing they are being asked to correct.
        // The caller that owns the mutation owns the message; it has already
        // shown it by the time this runs.
        //
        // Both halves are needed. The snapshot above is not a substitute for
        // this catch: without it the rejection propagates and the spinner hangs
        // again, which is the bug CRM-376 fixed.
        return;
      }

      if (mode === 'create') {
        reset(); // Reset form after successful creation
        return;
      }

      // Rebaseline on what was actually written. `defaultValues` are fixed at
      // mount, so a changed `initialData` prop never reaches react-hook-form:
      // without this the form keeps values the save deliberately dropped, and
      // the next save resends them. Only a successful save returns a snapshot,
      // so a failed one leaves the edits in place to retry.
      if (!saved) return;

      // The fields stay editable while the save is in flight, so anything typed
      // in the meantime is newer than the snapshot and must survive the reset.
      // It is written back afterwards and left dirty: it is not saved yet.
      const current = getValues();
      const typedWhileSaving = (
        Object.keys(current) as (keyof BuyerFormData)[]
      ).filter((field) => current[field] !== submitted[field]);

      reset(saved);

      typedWhileSaving.forEach((field) => {
        setValue(field, current[field], { shouldDirty: true });
      });
    },
    [onSubmit, mode, reset, getValues, setValue]
  );

  return {
    // Form methods
    register,
    handleSubmit: handleSubmit(handleFormSubmit),
    control,
    setValue,
    watch,
    reset,

    // Form state
    errors,
    isSubmitting,
    isDirty,

    // Computed values
    isEditMode: mode === 'edit',
  };
};
